import { useState } from 'react';
import {
  generateEmbedUrl,
  generateHtmlSnippet,
  generateReactSnippet,
  EmbedConfig
} from '../../utils/urlGenerator';
import { EMBED_BASE_URL } from '../../../../config';

/**
 * Embed Code Display Component
 *
 * Shows the generated embed code with tabs for HTML and React formats.
 * Includes copy-to-clipboard functionality with visual feedback.
 */

interface EmbedCodeDisplayProps {
  config: EmbedConfig;
}

type CodeFormat = 'html' | 'react' | 'htmlMobile' | 'reactMobile';

export default function EmbedCodeDisplay({ config }: EmbedCodeDisplayProps) {
  const [activeTab, setActiveTab] = useState<CodeFormat>('html');
  const [copied, setCopied] = useState(false);

  const baseUrl = EMBED_BASE_URL;

  // Strip the dashboard-only preview hint before generating the copy-able snippet.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { previewProductTag: _preview, ...snippetConfig } = config;
  const embedUrl = generateEmbedUrl(baseUrl, snippetConfig);
  const htmlCode = generateHtmlSnippet(embedUrl, config.width, config.height);
  const reactCode = generateReactSnippet(embedUrl, config.width, config.height);
  const htmlMobileCode = generateHtmlSnippet(
    embedUrl,
    config.width,
    config.height,
    { mobileAutoResize: true }
  );
  const reactMobileCode = generateReactSnippet(
    embedUrl,
    config.width,
    config.height,
    { mobileAutoResize: true }
  );

  const currentCode =
    activeTab === 'html'
      ? htmlCode
      : activeTab === 'react'
      ? reactCode
      : activeTab === 'htmlMobile'
      ? htmlMobileCode
      : reactMobileCode;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(currentCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for iframe contexts where Clipboard API is blocked by permissions policy
      try {
        const textarea = document.createElement('textarea');
        textarea.value = currentCode;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (fallbackErr) {
        console.error('Failed to copy:', fallbackErr);
      }
    }
  };

  return (
    <div className="flex h-full flex-col rounded-lg bg-white p-4 shadow-lg">
      {/* Header with Copy Button */}
      <div className="flex flex-col">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-primary text-l grow whitespace-nowrap font-semibold ">
            Embed Code
          </h2>
          <button
            onClick={handleCopy}
            className="bg-primary grow whitespace-nowrap rounded-lg px-4 py-2 text-xs font-semibold text-white transition hover:bg-[#013456] "
          >
            {copied ? 'Copied!' : 'Copy Code'}
          </button>
        </div>

        {/* Helper Text */}
        <p className="shrink flex-wrap py-1 text-xs text-gray-500">
          Copy this code and paste it into your website where you want the
          configurator to appear.
        </p>
      </div>

      {/* Language Tabs */}
      <div className="mb-2 flex gap-1 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('html')}
          className={`border-b-2 px-4 py-2 text-sm font-semibold transition ${
            activeTab === 'html'
              ? 'text-primary border-primary'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          HTML
        </button>
        <button
          onClick={() => setActiveTab('react')}
          className={`border-b-2 px-4 py-2 text-sm font-semibold transition ${
            activeTab === 'react'
              ? 'text-primary border-primary'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          React
        </button>
        {/* TODO: MOBILE STYLINGS DISABLED REENABLE WHEN SECURITY CONCERNS ADDRESSED */}
        {/* <button
          onClick={() => setActiveTab('htmlMobile')}
          className={`border-b-2 px-4 py-2 text-sm font-semibold transition ${
            activeTab === 'htmlMobile'
              ? 'text-primary border-primary'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          HTML w/ Mobile
        </button>
        <button
          onClick={() => setActiveTab('reactMobile')}
          className={`border-b-2 px-4 py-2 text-sm font-semibold transition ${
            activeTab === 'reactMobile'
              ? 'text-primary border-primary'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          React w/ Mobile
        </button>*/}
      </div>

      {/* Code Block - Scrollable */}
      <div className="min-h-0 flex-1 overflow-auto rounded-lg bg-gray-900 px-2 py-4 font-mono text-sm text-gray-100">
        <code className="whitespace-pre-wrap break-all">{currentCode}</code>
      </div>
    </div>
  );
}
